import { type FC, type ReactNode } from "react";

import { Text } from "@mantine/core";

import { SubmissionQuotaWindow } from "../../../gql-generated/types";
import { type SubmissionQuota } from "../../models/SubmissionQuota";

import { RelativeResetTime } from "./RelativeResetTime";
import { SubmissionQuotaContext } from "./SubmissionQuotaContext";
import styles from "./SubmissionQuotaInline.module.css";
import {
    LensPlusResetsDaily,
    LensPlusResetsMonthly,
    LensPlusResetsOnSchedule,
    NextReset,
    NonLensPlusResetsDaily,
    NonLensPlusResetsMonthly,
    NonLensPlusResetsOnSchedule,
} from "./messages.generated";

export interface QuotaResetEntry {
    context: SubmissionQuotaContext;
    quota: SubmissionQuota;
}

const cadenceMessages = {
    [SubmissionQuotaContext.Regular]: {
        [SubmissionQuotaWindow.CalendarDay]: <NonLensPlusResetsDaily />,
        [SubmissionQuotaWindow.CalendarMonth]: <NonLensPlusResetsMonthly />,
    },
    [SubmissionQuotaContext.LensPlus]: {
        [SubmissionQuotaWindow.CalendarDay]: <LensPlusResetsDaily />,
        [SubmissionQuotaWindow.CalendarMonth]: <LensPlusResetsMonthly />,
    },
} satisfies Record<SubmissionQuotaContext, Record<SubmissionQuotaWindow, ReactNode>>;

/** Widened deliberately: `satisfies` above still fails the build on a member this file forgets,
 * while the lookup stays honest about values that reach us from a schema newer than this bundle. */
const cadenceLookup: Partial<Record<SubmissionQuotaContext, Partial<Record<SubmissionQuotaWindow, ReactNode>>>> =
    cadenceMessages;

/** Names the pool without claiming a cadence, for a `limitingWindow` from a schema newer than this
 * bundle. Without it a paragraph would carry a bare reset time and the reader would have to guess
 * which pool it belongs to. */
const unattributedCadenceMessages = {
    [SubmissionQuotaContext.Regular]: <NonLensPlusResetsOnSchedule />,
    [SubmissionQuotaContext.LensPlus]: <LensPlusResetsOnSchedule />,
} satisfies Record<SubmissionQuotaContext, ReactNode>;

/** `limitingWindow` names the bucket that currently binds, so it reads as a pool cadence only while
 * lens-central gives each pool exactly one window (regular daily, Lens+ monthly). Add a second
 * window to a pool and this sentence follows whichever bucket is scarcer, not the pool. */
const cadenceFor = ({ context, quota }: QuotaResetEntry): ReactNode =>
    cadenceLookup[context]?.[quota.limitingWindow] ?? null;

/** Falls back to the pool name alone so a reset time is never rendered unattributed. An unknown
 * window with no reset time leaves nothing to say at all. */
const guidanceFor = (entry: QuotaResetEntry): ReactNode =>
    cadenceFor(entry) ?? (entry.quota.replenishAt ? unattributedCadenceMessages[entry.context] : null);

export const SubmissionQuotaResetTooltipContent: FC<{ entries: readonly QuotaResetEntry[] }> = ({ entries }) => (
    <div className={styles.tooltipContent}>
        {entries
            .filter((entry) => guidanceFor(entry) !== null)
            .map((entry) => (
                <Text component="p" size="xs" className={styles.tooltipIntro} key={entry.context}>
                    {guidanceFor(entry)}
                    {entry.quota.replenishAt ? (
                        <Text
                            component="time"
                            size="xs"
                            dateTime={entry.quota.replenishAt.toISOString()}
                            className={styles.tooltipReset}
                        >
                            <NextReset resetAt={<RelativeResetTime replenishAt={entry.quota.replenishAt} />} />
                        </Text>
                    ) : null}
                </Text>
            ))}
    </div>
);
